import { useLocation } from "wouter";
import { CSSProperties } from "react";
import type { Recipe } from "@/lib/store";
import { queries, useStoreQuery, recipeSafetyForSeason } from "@/lib/store";
import Icon from "@/components/mnf/Icon";
import { PillTag, Avatar } from "@/components/mnf/UI";
import AwardBadge from "@/components/mnf/AwardBadge";
import SafetyBadge from "@/components/mnf/SafetyBadge";

/**
 * RecipeCard
 *
 * Used across the Nourish discovery hub for the Trending rail, Saved list,
 * Season filter, and any other place we need a recipe entry-row. Two layouts:
 *
 *   variant="hero"     Tall, image-led, used for Today's Harvest. Caption sits
 *                      on a forest gradient with the AwardBadge anchored top-right.
 *   variant="compact"  Short, horizontal, used in lists. Gradient swatch on the
 *                      left, title plus author plus tier on the right.
 *
 * Both variants navigate to /nourish/:recipeId on click.
 */

export default function RecipeCard({
  recipe,
  variant = "compact",
  className = "",
  style,
  eyebrow,
  badge,
}: {
  recipe: Recipe;
  variant?: "hero" | "compact";
  className?: string;
  style?: CSSProperties;
  eyebrow?: string;
  /** Optional override label that replaces the source pill on compact cards. */
  badge?: string;
}) {
  const [, setLocation] = useLocation();
  const author = queries.recipeAuthor(recipe.authorId);
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account. seasonState drives pregnancy / postpartum
  // food safety advisories, so falling back to the seeded demo account would
  // show one sister's advisories to another — either warning about a food
  // that is fine for her, or staying silent about one that is not. With no
  // known user, seasonState is null and recipeSafetyForSeason returns no
  // advisories rather than someone else's.
  const season = useStoreQuery(() => (me?.id ? queries.seasonState(me.id) : null));
  const advisories = recipeSafetyForSeason(recipe, season);
  const open = () => setLocation(`/nourish/${recipe.id}`);

  if (variant === "hero") {
    return (
      <button
        type="button"
        data-testid={`card-recipe-hero-${recipe.id}`}
        onClick={open}
        className={`relative overflow-hidden mnf-fade-up text-left w-full block hover-elevate ${className}`}
        style={{
          borderRadius: 24,
          background: recipe.heroImageUrl
            ? undefined
            : "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.65) 100%), " +
              recipe.heroGradient,
          border: "1px solid rgba(30,74,53,0.10)",
          boxShadow: "0 14px 36px rgba(30,74,53,0.18)",
          cursor: "pointer",
          ...style,
        }}
      >
        {/* Hero photo when curated. The forest gradient overlay below keeps the
         *  caption readable. Fallback (no image) renders the emoji huddle. */}
        {recipe.heroImageUrl && (
          <>
            <img
              src={recipe.heroImageUrl}
              alt=""
              aria-hidden
              loading="lazy"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.55) 75%, rgba(22,56,40,0.85) 100%)",
              }}
            />
          </>
        )}
        {/* Award chip floats top-right so the tier reads instantly. */}
        {recipe.awardTier !== "none" && (
          <div style={{ position: "absolute", top: 14, right: 14, zIndex: 2 }}>
            <AwardBadge tier={recipe.awardTier} count={recipe.lovedCount} size="sm" />
          </div>
        )}
        {/* Season-aware safety advisory floats top-left so a sister sees it before tapping. */}
        {advisories.length > 0 && (
          <div style={{ position: "absolute", top: 14, left: 14, zIndex: 2 }}>
            <SafetyBadge advisories={advisories} size="md" />
          </div>
        )}
        {/* Decorative emoji huddle on a warm pillow. Hidden when a hero photo
         *  is rendered, replaced by an empty spacer so the caption stays low. */}
        {recipe.heroImageUrl ? (
          <div style={{ height: 180 }} />
        ) : (
        <div className="relative flex items-end justify-center" style={{ height: 180, paddingBottom: 18 }}>
          <div
            style={{
              width: 200, height: 90,
              background: "radial-gradient(ellipse at 50% 40%, #C0784A 5%, #8a4f2a 55%, #4a2914 100%)",
              borderRadius: "50% 50% 46% 46% / 50% 50% 90% 90%",
              position: "relative",
              boxShadow: "0 12px 22px rgba(0,0,0,0.40), inset 0 -10px 14px rgba(0,0,0,0.32)",
            }}
          >
            {recipe.heroEmojis.slice(0, 4).map((emo, i) => {
              const positions: CSSProperties[] = [
                { top: -52, left: 22, fontSize: 56 },
                { top: -58, left: 78, fontSize: 52 },
                { top: -44, right: 18, fontSize: 46 },
                { top: -30, left: 145, fontSize: 30 },
              ];
              return (
                <div key={i} style={{ position: "absolute", ...positions[i] }} aria-hidden>
                  {emo}
                </div>
              );
            })}
          </div>
        </div>
        )}
        <div className="relative px-5 pt-1 pb-5">
          {eyebrow && (
            <p className="mnf-eyebrow" style={{ color: "rgba(245,240,232,0.85)" }}>
              {eyebrow}
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <PillTag tone="coral">
              <Icon name="schedule" size={11} /> {recipe.durationMin} min
            </PillTag>
            <PillTag tone="forest">
              <Icon name="diversity_1" size={11} color="#F5F0E8" /> Serves {recipe.servings}
            </PillTag>
          </div>
          <h2
            className="mnf-serif mt-2.5"
            style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.15, color: "#fff" }}
          >
            {recipe.title}
          </h2>
          <p
            className="text-[11px] mnf-body mt-1.5"
            style={{
              color: "rgba(255,255,255,0.85)",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            {recipe.kcal} kcal · {recipe.proteinG}g protein · {recipe.fiberG}g fiber per serving
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Avatar initials={author.initials} size={26} />
            <span className="mnf-body" style={{ fontSize: 12, color: "rgba(245,240,232,0.85)" }}>
              {author.isCoach ? "By Coach Kiley" : `By ${author.name}`}
            </span>
          </div>
        </div>
      </button>
    );
  }

  // Compact variant
  return (
    <button
      type="button"
      data-testid={`card-recipe-${recipe.id}`}
      onClick={open}
      className={`mnf-glass w-full text-left hover-elevate ${className}`}
      style={{ padding: 12, display: "flex", gap: 12, alignItems: "stretch", cursor: "pointer", ...style }}
    >
      {/* Photo swatch when curated, gradient swatch otherwise. */}
      <div
        style={{
          width: 84,
          minHeight: 84,
          borderRadius: 14,
          background: recipe.heroImageUrl
            ? undefined
            : "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%), " +
              recipe.heroGradient,
          flexShrink: 0,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {recipe.heroImageUrl ? (
          <img
            src={recipe.heroImageUrl}
            alt=""
            aria-hidden
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 30,
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.35))",
            }}
          >
            {recipe.heroEmojis[0] ?? "\ud83c\udf3f"}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <PillTag tone="neutral">
              <Icon name="schedule" size={10} /> {recipe.durationMin}m
            </PillTag>
            {badge ? (
              <PillTag tone="purple">{badge}</PillTag>
            ) : (
              <>
                {recipe.source === "kiley" && (
                  <PillTag tone="coral">Coach pick</PillTag>
                )}
                {recipe.source === "imported" && (
                  <PillTag tone="purple">Imported</PillTag>
                )}
              </>
            )}
            {advisories.length > 0 && <SafetyBadge advisories={advisories} size="sm" />}
          </div>
          <p
            className="mnf-serif mt-1.5"
            style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.25 }}
          >
            {recipe.title}
          </p>
        </div>
        <div className="flex items-center justify-between gap-2 mt-1.5">
          <span
            className="mnf-body"
            style={{ fontSize: 11, color: "var(--mnf-tg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {author.isCoach ? "Coach Kiley" : author.name}
          </span>
          {recipe.awardTier !== "none" && (
            <AwardBadge tier={recipe.awardTier} count={recipe.lovedCount} size="sm" showCount={false} />
          )}
        </div>
      </div>
    </button>
  );
}
